// Lightweight PDP nutrition table renderer using Storefront API
// Mirrors the logic and HTML structure from scripts/sections/quick-view.js

(function () {
  const container = document.getElementById('pdp-nutrition-table');
  if (!container) return;

  const productId = container.getAttribute('data-product-id');
  if (!productId) return;

  const STOREFRONT_TOKEN = '0fdc6cbe1d1380ee37682f02c8e9b3be';
  const API_VERSION = '2024-01';

  const esc = (s) =>
    String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  const isTrueish = (v) => v === true || String(v).toLowerCase() === 'true';

  const fieldsArrayToObject = (fields) => {
    const obj = {};
    (fields || []).forEach((f) => {
      obj[f.key] = f.value;
    });
    return obj;
  };

  const escapeHtml = (s = '') =>
    String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  const escapeAttr = (s = '') =>
    String(s)
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');

  const renderShopifyRichNode = (node) => {
    if (!node) return '';

    const renderChildren = (n) =>
      (n.children || []).map(renderShopifyRichNode).join('');

    switch (node.type) {
      case 'root':
        return renderChildren(node);
      case 'paragraph':
        return `<p>${renderChildren(node)}</p>`;
      case 'text': {
        let out = escapeHtml(node.value || '');
        if (node.bold) out = `<strong>${out}</strong>`;
        if (node.italic) out = `<em>${out}</em>`;
        if (node.underline) out = `<u>${out}</u>`;
        if (node.code) out = `<code>${out}</code>`;
        return out;
      }
      case 'heading': {
        const level = Math.min(Math.max(node.level || 2, 1), 6);
        return `<h${level}>${renderChildren(node)}</h${level}>`;
      }
      case 'list': {
        const tag = node.listType === 'ordered' ? 'ol' : 'ul';
        return `<${tag}>${renderChildren(node)}</${tag}>`;
      }
      case 'list-item':
        return `<li>${renderChildren(node)}</li>`;
      case 'link': {
        const href = escapeAttr(node.url || '#');
        const target = node.target ? ` target="${escapeAttr(node.target)}" rel="noopener noreferrer"` : '';
        return `<a href="${href}"${target}>${renderChildren(node)}</a>`;
      }
      case 'blockquote':
        return `<blockquote>${renderChildren(node)}</blockquote>`;
      case 'horizontalRule':
        return `<hr/>`;
      default:
        return renderChildren(node);
    }
  };

  const renderShopifyRichTextToHtml = (raw) => {
    if (!raw) return '';
    try {
      const json = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return renderShopifyRichNode(json);
    } catch {
      // If it's already HTML or plain text, return as-is
      return String(raw);
    }
  };

  const query = `
    query getProductById($id: ID!) {
      product(id: $id) {
        id
        nutrition_facts_calories: metafield(key: "calories_per_serving", namespace: "nutrition_table") {
          value
        }
        nutrition_facts_calories_from_fat: metafield(key: "calories_from_fat", namespace: "nutrition_table") {
          value
        }
        nutrition_facts_macros: metafield(key: "macronutrients", namespace: "nutrition_table") {
          references (first: 50) {
            nodes {
              ... on Metaobject {
                id
                fields {
                  key
                  value
                }
              }
            }
          }
        }
        nutrition_facts_vitamins_minerals: metafield(key: "vitamins_minerals", namespace: "nutrition_table") {
          references (first: 50) {
            nodes {
              ... on Metaobject {
                id
                fields {
                  key
                  value
                }
              }
            }
          }
        }
        nutrition_facts_other_label_information: metafield(key: "other_label_information", namespace: "nutrition_table") {
          value
        }
        nutrition_facts_serving_size: metafield(key: "header_serving_size", namespace: "nutrition_table") {
          value
        }
        nutrition_facts_servings_per_container: metafield(key: "header_servings_per_container", namespace: "nutrition_table") {
          value
        }
        nutrition_table_ingredients_label_information: metafield(namespace: "custom", key: "nutrition_table_ingredients_label_information") {
          value
        }
        nutrition_tables_list: metafield(namespace: "custom", key: "nutrition_table") {
          references(first: 50) {
            nodes {
              ... on Metaobject {
                id
                fields {
                  key
                  value
                }
                macros: field(key: "macronutrients") {
                  key
                  references(first: 50) {
                    nodes {
                      ... on Metaobject {
                        id
                        fields {
                          key
                          value
                        }
                      }
                    }
                  }
                }
                vitamins: field(key: "vitamins_minerals") {
                  key
                  references(first: 50) {
                    nodes {
                      ... on Metaobject {
                        id
                        fields {
                          key
                          value
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  fetch(`/api/${API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
    },
    body: JSON.stringify({
      query,
      variables: {
        id: `gid://shopify/Product/${productId}`,
      },
    }),
  })
    .then((res) => res.json())
    .then((res) => {
      const product = res?.data?.product;
      if (!product) return;

      const calories = product.nutrition_facts_calories?.value;
      const caloriesFromFat = product.nutrition_facts_calories_from_fat?.value;
      const macrosNodes = product.nutrition_facts_macros?.references?.nodes || [];
      const vitaminsNodes = product.nutrition_facts_vitamins_minerals?.references?.nodes || [];
      const otherInfoRaw = product.nutrition_facts_other_label_information?.value;
      const servingSize = product.nutrition_facts_serving_size?.value;
      const servingsPerContainer = product.nutrition_facts_servings_per_container?.value;
      const ingredientsRaw = product.nutrition_table_ingredients_label_information?.value;

      if (!calories && !servingSize && !servingsPerContainer && !macrosNodes.length && !vitaminsNodes.length && !otherInfoRaw && !ingredientsRaw) {
        return;
      }

      const macros = macrosNodes.map((node) => ({
        id: node.id,
        ...fieldsArrayToObject(node.fields || []),
      }));

      const vitamins = vitaminsNodes.map((node) => ({
        id: node.id,
        ...fieldsArrayToObject(node.fields || []),
      }));

      const nutritionColumns = [
        {
          servingSize,
          servingsPerContainer,
          calories,
          macros,
          vitaminsMinerals: vitamins,
          otherInfo: otherInfoRaw,
          ingredientsInfo: ingredientsRaw,
        },
      ];

      // Add additional nutrition tables from custom.nutrition_table metaobject list
      if (product.nutrition_tables_list?.references?.nodes) {
        product.nutrition_tables_list.references.nodes.forEach((metaobjectNode) => {
          const fieldsObject = fieldsArrayToObject(metaobjectNode.fields || []);
          const getField = (key) =>
            (fieldsObject[key] && fieldsObject[key].value !== undefined
              ? fieldsObject[key].value
              : fieldsObject[key]) || '';

          const servingSizeExtra = getField('header_serving_size');
          const servingsPerContainerExtra = getField('header_servings_per_container');
          const caloriesExtra = getField('calories_per_serving');
          const otherInfoExtra = getField('other_label_information');
          const ingredientsInfoExtra = getField('ingredients_label_information');

          let macrosItems = [];
          if (metaobjectNode.macros?.references?.nodes) {
            macrosItems = metaobjectNode.macros.references.nodes.map((node) => ({
              id: node.id,
              ...fieldsArrayToObject(node.fields || []),
            }));
          }

          let vitaminsItems = [];
          if (metaobjectNode.vitamins?.references?.nodes) {
            vitaminsItems = metaobjectNode.vitamins.references.nodes.map((node) => ({
              id: node.id,
              ...fieldsArrayToObject(node.fields || []),
            }));
          }

          nutritionColumns.push({
            servingSize: servingSizeExtra,
            servingsPerContainer: servingsPerContainerExtra,
            calories: caloriesExtra,
            macros: macrosItems,
            vitaminsMinerals: vitaminsItems,
            otherInfo: otherInfoExtra,
            ingredientsInfo: ingredientsInfoExtra,
          });
        });
      }

      const renderMacroItem = (item) => {
        const baseTitle = item.title;
        const displayTitle =
          baseTitle === 'Custom'
            ? item.custom_title
              ? String(item.custom_title)
              : null
            : baseTitle;

        if (!displayTitle) return '';

        let indentClass = '';
        if (item.indented === 'Once') {
          indentClass = ' nutrition-facts-table__macros-item--indent';
        } else if (item.indented === 'Twice') {
          indentClass = ' nutrition-facts-table__macros-item--indent-large';
        }

        const highlightClass = isTrueish(item.bold_title)
          ? ' nutrition-facts-table__macros-item-label--highlight'
          : '';

        const percentValue = item.percent ?? item.percentage;
        const right =
          percentValue !== undefined && percentValue !== ''
            ? `<span class="nutrition-facts-table__macros-item-label">${esc(percentValue)}%</span>`
            : '';

        const isAddedSugars = String(baseTitle || '').trim().toLowerCase() === 'added sugars';
        const titleText = isAddedSugars
          ? `Includes ${item.value ? `${item.value} ` : ''}${displayTitle}`
          : displayTitle;

        const valueSpan =
          !isAddedSugars && item.value
            ? `<span class="nutrition-facts-table__macros-item-label">${esc(item.value)}</span>`
            : '';

        return `<div class="nutrition-facts-table__macros-item${indentClass}">
          <span class="nutrition-facts-table__macros-item-title">
            <span class="nutrition-facts-table__macros-item-label${highlightClass}">${esc(titleText)}</span>
            ${valueSpan}
          </span>
          ${right}
        </div>`;
      };

      const renderVitaminItem = (item) => {
        const baseTitle = item.title;
        const displayTitle =
          baseTitle === 'Custom'
            ? item.custom_title
              ? String(item.custom_title)
              : null
            : baseTitle;

        if (!displayTitle) return '';

        const percentValue = item.percentage ?? item.percent;
        const right =
          percentValue !== undefined && percentValue !== ''
            ? `<span class="nutrition-facts-table__macros-item-label">${esc(percentValue)}%</span>`
            : '';

        const left = `${esc(displayTitle)}${item.value ? ' ' + esc(item.value) : ''}`;

        return `<div class="nutrition-facts-table__macros-item">
          <span class="nutrition-facts-table__macros-item-title">
            ${left}
          </span>
          ${right}
        </div>`;
      };

      const servingSizeSection = nutritionColumns
        .map((col) => {
          const pre = col.servingsPerContainer
            ? `<div class="nutrition-facts-table__section-preheader">${esc(col.servingSize)}</div>`
            : '';
          const amount = col.servingSize
            ? `<span class="nutrition-facts-table__section-header-amount">${esc(col.servingsPerContainer)}</span>`
            : '';
          return `<div class="nutrition-facts-table__column">
            ${pre}
            <div class="nutrition-facts-table__section-header">
              <span class="nutrition-facts-table__section-header-title">Serving Size</span>
              ${amount}
            </div>
          </div>`;
        })
        .join('');

      const caloriesSection = nutritionColumns
        .map((col) => {
          const amount = col.calories
            ? `<span class="nutrition-facts-table__section-header-amount">${esc(col.calories)}</span>`
            : '';
          return `<div class="nutrition-facts-table__column">
            <div class="nutrition-facts-table__section-preheader">Amount per serving</div>
            <div class="nutrition-facts-table__section-header">
              <span class="nutrition-facts-table__section-header-title">Calories</span>
              ${amount}
            </div>
          </div>`;
        })
        .join('');

      let macrosSection = '';
      const hasMacros = nutritionColumns.some((col) => col.macros && col.macros.length > 0);
      if (hasMacros) {
        macrosSection = nutritionColumns
          .map((col) => {
            const items =
              col.macros?.map((item) => renderMacroItem(item)).filter(Boolean).join('') || '';
            const header = `<div class="nutrition-facts-table__macros-item">
              <span>&nbsp;</span>
              <span class="nutrition-facts-table__macros-item-label">% Daily Value</span>
            </div>`;
            return `<div class="nutrition-facts-table__column">
              ${header}
              ${items}
            </div>`;
          })
          .join('');
      }

      let vitaminsSection = '';
      const hasVitamins = nutritionColumns.some(
        (col) => col.vitaminsMinerals && col.vitaminsMinerals.length > 0,
      );
      if (hasVitamins) {
        vitaminsSection = nutritionColumns
          .map((col) => {
            const items =
              col.vitaminsMinerals
                ?.map((item) => renderVitaminItem(item))
                .filter(Boolean)
                .join('') || '';
            return `<div class="nutrition-facts-table__column">${items}</div>`;
          })
          .join('');
      }

      const otherInfoFooterSection = nutritionColumns
        .map((col) => {
          const html = col.otherInfo ? renderShopifyRichTextToHtml(col.otherInfo) : '';
          return `<div class="nutrition-facts-table__column">${
            html ? `<p>${html}</p>` : ''
          }</div>`;
        })
        .join('');

      const ingredientsFooterSection = nutritionColumns
        .map((col) => {
          const html = col.ingredientsInfo ? renderShopifyRichTextToHtml(col.ingredientsInfo) : '';
          const title = html
            ? '<div class="nutrition-facts-table__footer-title">Ingredients:</div>'
            : '';
          const content = html ? `<div>${html}</div>` : '';
          return `<div class="nutrition-facts-table__column">
            ${title}
            ${content}
          </div>`;
        })
        .join('');

      // Helper to check if HTML string has actual text content (not just empty divs/whitespace)
      const hasContent = (html) => {
        if (!html) return false;
        const temp = document.createElement('div');
        temp.innerHTML = html;
        const text = temp.textContent || temp.innerText || '';
        return text.trim().length > 0;
      };

      const sections = [
        `<section class="nutrition-facts-table__section nutrition-facts-table__section--multiple">${servingSizeSection}</section>`,
        `<section class="nutrition-facts-table__section nutrition-facts-table__section--multiple">${caloriesSection}</section>`,
      ];

      if (macrosSection) {
        sections.push(
          `<section class="nutrition-facts-table__macros nutrition-facts-table__section--multiple">${macrosSection}</section>`,
        );
      }

      if (vitaminsSection) {
        sections.push(
          `<section class="nutrition-facts-table__macros nutrition-facts-table__section--multiple">${vitaminsSection}</section>`,
        );
      }

      if (otherInfoFooterSection && hasContent(otherInfoFooterSection)) {
        sections.push(
          `<section class="nutrition-facts-table__footer nutrition-facts-table__section--multiple">${otherInfoFooterSection}</section>`,
        );
      }

      if (ingredientsFooterSection && hasContent(ingredientsFooterSection)) {
        sections.push(
          `<section class="nutrition-facts-table__footer nutrition-facts-table__section--multiple">${ingredientsFooterSection}</section>`,
        );
      }

      // Build single-column table for mobile carousel (mirrors quick view modal)
      const buildSingleNutritionTableHTML = (col) => {
        const servingPreHeader =
          col.servingsPerContainer && col.servingSize
            ? `<div class="nutrition-facts-table__section-preheader">${esc(col.servingSize)}</div>`
            : '';
        const servingAmount =
          col.servingSize && col.servingsPerContainer
            ? `<span class="nutrition-facts-table__section-header-amount">${esc(
                col.servingsPerContainer,
              )}</span>`
            : '';

        const servingSizeSectionSingle = `<section class="nutrition-facts-table__section">
          ${servingPreHeader}
          <div class="nutrition-facts-table__section-header">
            <span class="nutrition-facts-table__section-header-title">Serving Size</span>
            ${servingAmount}
          </div>
        </section>`;

        const caloriesAmount = col.calories
          ? `<span class="nutrition-facts-table__section-header-amount">${esc(col.calories)}</span>`
          : '';

        const caloriesSectionSingle = `<section class="nutrition-facts-table__section">
          <div class="nutrition-facts-table__section-preheader">Amount per serving</div>
          <div class="nutrition-facts-table__section-header">
            <span class="nutrition-facts-table__section-header-title">Calories</span>
            ${caloriesAmount}
          </div>
        </section>`;

        let macrosSectionSingle = '';
        if (col.macros && col.macros.length > 0) {
          const items =
            col.macros.map((item) => renderMacroItem(item)).filter(Boolean).join('') || '';
          const header = `<div class="nutrition-facts-table__macros-item">
            <span>&nbsp;</span>
            <span class="nutrition-facts-table__macros-item-label">% Daily Value</span>
          </div>`;
          macrosSectionSingle = `<section class="nutrition-facts-table__macros">${header}${items}</section>`;
        }

        let vitaminsSectionSingle = '';
        if (col.vitaminsMinerals && col.vitaminsMinerals.length > 0) {
          const items =
            col.vitaminsMinerals
              .map((item) => renderVitaminItem(item))
              .filter(Boolean)
              .join('') || '';
          vitaminsSectionSingle = `<section class="nutrition-facts-table__macros">${items}</section>`;
        }

        let otherInfoSectionSingle = '';
        if (col.otherInfo) {
          const otherInfoHtml = renderShopifyRichTextToHtml(col.otherInfo) || '';
          if (otherInfoHtml) {
            const temp = document.createElement('div');
            temp.innerHTML = otherInfoHtml;
            const text = temp.textContent || temp.innerText || '';
            if (text.trim().length > 0) {
              otherInfoSectionSingle = `<section class="nutrition-facts-table__footer">${otherInfoHtml}</section>`;
            }
          }
        }

        let ingredientsSectionSingle = '';
        if (col.ingredientsInfo) {
          const ingredientsHtml = renderShopifyRichTextToHtml(col.ingredientsInfo) || '';
          if (ingredientsHtml) {
            const temp = document.createElement('div');
            temp.innerHTML = ingredientsHtml;
            const text = temp.textContent || temp.innerText || '';
            if (text.trim().length > 0) {
              ingredientsSectionSingle = `<section class="nutrition-facts-table__footer">
                <div class="nutrition-facts-table__footer-title">Ingredients:</div>
                <div>${ingredientsHtml}</div>
              </section>`;
            }
          }
        }

        return `<div class="nutrition-facts-table">
          <header class="nutrition-facts-table__header">Nutrition Facts</header>
          ${servingSizeSectionSingle}
          ${caloriesSectionSingle}
          ${macrosSectionSingle}
          ${vitaminsSectionSingle}
          ${otherInfoSectionSingle}
          ${ingredientsSectionSingle}
        </div>`;
      };

      const isMobile = window.innerWidth < 768;
      const hasMultipleColumns = nutritionColumns.length > 1;

      let html;

      if (isMobile && hasMultipleColumns) {
        const slidesHTML = nutritionColumns
          .map((col, index) => {
            const tableHTML = buildSingleNutritionTableHTML(col);
            return `<div class="nutrition-facts-carousel-slide" data-slide-index="${index}" style="display: ${
              index === 0 ? 'block' : 'none'
            }">
              <div class="quickview-modal__nutrition-table">${tableHTML}</div>
            </div>`;
          })
          .join('');

        const paginationHTML = nutritionColumns
          .map((_, index) => {
            return `<button type="button" class="nutrition-facts-carousel-dot ${
              index === 0 ? 'is-active' : ''
            }" data-slide-index="${index}" aria-label="Go to nutrition facts table ${
              index + 1
            }"></button>`;
          })
          .join('');

        html = `<div class="nutrition-facts-carousel-container">
          <div class="nutrition-facts-carousel-slides">${slidesHTML}</div>
          <div class="nutrition-facts-carousel-pagination">${paginationHTML}</div>
        </div>`;
      } else {
        html = `<div class="quickview-modal__nutrition-table nutrition-facts-table">
          <header class="nutrition-facts-table__header">Nutrition Facts</header>
          ${sections.join('')}
        </div>`;
      }

      container.innerHTML = html;

      // Basic carousel behavior for PDP (mirrors quick view modal)
      const carouselContainer = container.querySelector('.nutrition-facts-carousel-container');
      if (carouselContainer) {
        const slides = carouselContainer.querySelectorAll('.nutrition-facts-carousel-slide');
        const dots = carouselContainer.querySelectorAll('.nutrition-facts-carousel-dot');
        if (slides.length > 1) {
          let currentSlide = 0;
          let touchStartX = 0;
          let touchEndX = 0;

          const goToSlide = (index) => {
            if (index < 0 || index >= slides.length) return;
            currentSlide = index;
            slides.forEach((slide, i) => {
              slide.style.display = i === currentSlide ? 'block' : 'none';
            });
            dots.forEach((dot, i) => {
              dot.classList.toggle('is-active', i === currentSlide);
            });
          };

          dots.forEach((dot, index) => {
            dot.addEventListener('click', () => goToSlide(index));
          });

          carouselContainer.addEventListener('touchstart', (e) => {
            touchStartX = e.touches[0].clientX;
          });

          carouselContainer.addEventListener('touchmove', (e) => {
            touchEndX = e.touches[0].clientX;
          });

          carouselContainer.addEventListener('touchend', () => {
            if (!touchStartX || !touchEndX) return;
            const distance = touchStartX - touchEndX;
            const minSwipeDistance = 50;
            if (Math.abs(distance) > minSwipeDistance) {
              if (distance > 0 && currentSlide < slides.length - 1) {
                goToSlide(currentSlide + 1);
              } else if (distance < 0 && currentSlide > 0) {
                goToSlide(currentSlide - 1);
              }
            }
            touchStartX = 0;
            touchEndX = 0;
          });
        }
      }

      // If a structured nutrition table is present, remove fallback nutrition images
      try {
        const nutritionTab = container.closest('.product__tabs-content[data-tab-content="nutrition"]');
        if (nutritionTab && container.innerHTML.trim() !== '') {
          nutritionTab.querySelectorAll('.product__tabs-content__image').forEach((el) => el.remove());
        }
      } catch (e) {
        console.warn('Unable to remove fallback nutrition images:', e);
      }
    })
    .catch((e) => {
      console.error('Error fetching PDP nutrition data:', e);
    });
})();

